import type { ChartType } from 'tio-common/types/chart';
import type { DateGroupingInterval } from './date-grouping';

// Unified configuration interface for both chart and table reports
export interface UnifiedReportConfig {
  // View state
  viewType: 'table' | 'chart';

  // Chart-specific settings (when viewType = 'chart')
  chartSettings?: {
    chartType: ChartType;
    groupByField: string;
    dateGrouping?: DateGroupingInterval;
    showTableView?: boolean;
  };

  // Table-specific settings (when viewType = 'table')
  tableSettings?: {
    showIndividualApplications: boolean;
    selectedColumns?: string[];
    columnOrder?: string[];
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  };

  // Shared filter state (works for both views)
  filters: {
    // Program filters
    programTemplateId?: string;
    programCreatedAtStart?: string;
    programCreatedAtEnd?: string;
    excludeNonVoucherPrograms?: boolean;
    courseCashProgramsOnly?: boolean;

    // Application filters
    applicationStates?: string[];
    applicationCreatedAtStart?: string;
    applicationCreatedAtEnd?: string;
    applicationDateRangePreset?: string; // Relative date preset ID
    inCollections?: boolean;

    // Payment filters
    includePayments?: boolean;
    paymentStates?: string[];
    paymentYear?: string;

    // Course filters
    includeCourses?: boolean;
    coursesBeginDate?: string;
    coursesEndDate?: string;

    // Employee filters
    employeeId?: string;
    terminatedEmployees?: boolean;
    excludeTermVerified?: boolean;
    approverId?: string;

    // General filters
    timeRange?: string;
    searchText?: string;
  };

  exportSettings?: {
    format: 'csv' | 'excel' | 'pdf';
    includeCharts?: boolean;
    dateRange?: string;
  };
}

// Legacy chart configuration interface (for backward compatibility)
export interface ChartBuilderConfig {
  dataset: string;
  groupByField: string;
  chartType: ChartType;
  timeRange: string;
  dateGrouping: DateGroupingInterval;
  metadata?: {
    [key: string]: unknown;
  };
}

// Legacy TAS programs query parameters
export interface TasProgramsQueryParams {
  page?: number;
  limit?: number;

  // Program filters
  programTemplateId?: string;
  programCreatedAtStart?: string;
  programCreatedAtEnd?: string;
  excludeNonVoucherPrograms?: boolean;
  courseCashProgramsOnly?: boolean;

  // Application filters
  showIndividualApplications?: boolean;
  applicationStates?: string;
  applicationCreatedAtStart?: string;
  applicationCreatedAtEnd?: string;
  applicationDateRangePreset?: string; // Relative date preset ID
  inCollections?: boolean;

  // Payment filters
  includePayments?: boolean;
  paymentStates?: string;
  paymentYear?: string;

  // Course filters
  includeCourses?: boolean;
  coursesBeginDate?: string;
  coursesEndDate?: string;

  // Employee filters
  employeeId?: string;
  terminatedEmployees?: boolean;
  excludeTermVerified?: boolean;
  approverId?: string;

  // Invoice filters
  includeInvoiceDate?: boolean;

  // Column display settings
  selectedColumns?: string;
  columnOrder?: string[];
}

/**
 * Configuration manager for unified reporting
 * Handles conversion between different config formats and provides standardized access
 */
export class ReportingConfigManager {
  /**
   * Convert legacy chart builder config to unified format
   */
  static fromChartBuilder(config: ChartBuilderConfig): UnifiedReportConfig {
    return {
      viewType: 'chart',
      chartSettings: {
        chartType: config.chartType,
        groupByField: config.groupByField,
        dateGrouping: config.dateGrouping,
        showTableView: false,
      },
      filters: {
        timeRange: config.timeRange,
      },
    };
  }

  /**
   * Convert legacy TAS programs params to unified format
   */
  static fromTasPrograms(params: TasProgramsQueryParams): UnifiedReportConfig {
    return {
      viewType: 'table',
      tableSettings: {
        showIndividualApplications: params.showIndividualApplications || false,
        columnOrder: params.columnOrder,
      },
      filters: {
        programTemplateId: params.programTemplateId,
        programCreatedAtStart: params.programCreatedAtStart,
        programCreatedAtEnd: params.programCreatedAtEnd,
        excludeNonVoucherPrograms: params.excludeNonVoucherPrograms,
        courseCashProgramsOnly: params.courseCashProgramsOnly,

        applicationStates: params.applicationStates?.split(',').filter(Boolean),
        applicationCreatedAtStart: params.applicationCreatedAtStart,
        applicationCreatedAtEnd: params.applicationCreatedAtEnd,
        applicationDateRangePreset: params.applicationDateRangePreset,
        inCollections: params.inCollections,

        includePayments: params.includePayments,
        paymentStates: params.paymentStates?.split(',').filter(Boolean),
        paymentYear: params.paymentYear,

        includeCourses: params.includeCourses,
        coursesBeginDate: params.coursesBeginDate,
        coursesEndDate: params.coursesEndDate,

        employeeId: params.employeeId,
        terminatedEmployees: params.terminatedEmployees,
        excludeTermVerified: params.excludeTermVerified,
        approverId: params.approverId,
      },
    };
  }

  /**
   * Convert unified config back to chart builder format
   */
  static toChartBuilder(config: UnifiedReportConfig): ChartBuilderConfig {
    const chartSettings = config.chartSettings || {
      chartType: 'bar' as ChartType,
      groupByField: 'application_state',
      dateGrouping: 'day' as DateGroupingInterval,
    };

    return {
      dataset: 'tas-program-instances', // Default to TAS data
      groupByField: chartSettings.groupByField,
      chartType: chartSettings.chartType,
      timeRange: config.filters.timeRange || 'all',
      dateGrouping: chartSettings.dateGrouping || 'day',
      metadata: {
        filters: config.filters,
      },
    };
  }

  /**
   * Convert unified config back to TAS programs query params
   */
  static toTasPrograms(config: UnifiedReportConfig): TasProgramsQueryParams {
    const filters = config.filters;
    const tableSettings = config.tableSettings;

    return {
      // Table settings
      showIndividualApplications: tableSettings?.showIndividualApplications,
      columnOrder: tableSettings?.columnOrder,

      // Program filters
      programTemplateId: filters.programTemplateId,
      programCreatedAtStart: filters.programCreatedAtStart,
      programCreatedAtEnd: filters.programCreatedAtEnd,
      excludeNonVoucherPrograms: filters.excludeNonVoucherPrograms,
      courseCashProgramsOnly: filters.courseCashProgramsOnly,

      // Application filters
      applicationStates: filters.applicationStates?.join(','),
      applicationCreatedAtStart: filters.applicationCreatedAtStart,
      applicationCreatedAtEnd: filters.applicationCreatedAtEnd,
      applicationDateRangePreset: filters.applicationDateRangePreset,
      inCollections: filters.inCollections,

      // Payment filters
      includePayments: filters.includePayments,
      paymentStates: filters.paymentStates?.join(','),
      paymentYear: filters.paymentYear,

      // Course filters
      includeCourses: filters.includeCourses,
      coursesBeginDate: filters.coursesBeginDate,
      coursesEndDate: filters.coursesEndDate,

      // Employee filters
      employeeId: filters.employeeId,
      terminatedEmployees: filters.terminatedEmployees,
      excludeTermVerified: filters.excludeTermVerified,
      approverId: filters.approverId,
    };
  }

  /**
   * Create URL search params from unified config
   */
  static toUrlParams(config: UnifiedReportConfig): URLSearchParams {
    const params = new URLSearchParams();

    // View type
    params.set('viewType', config.viewType);

    // Chart settings
    if (config.chartSettings) {
      params.set('chartType', config.chartSettings.chartType);
      params.set('groupByField', config.chartSettings.groupByField);
      if (config.chartSettings.dateGrouping) {
        params.set('dateGrouping', config.chartSettings.dateGrouping);
      }
      if (config.chartSettings.showTableView) {
        params.set('showTableView', 'true');
      }
    }

    // Table settings
    if (config.tableSettings) {
      if (config.tableSettings.showIndividualApplications) {
        params.set('showIndividualApplications', 'true');
      }
    }

    // Filters (only set non-empty values)
    Object.entries(config.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','));
          }
        } else {
          params.set(key, String(value));
        }
      }
    });

    return params;
  }

  /**
   * Parse URL search params into unified config
   */
  static fromUrlParams(params: URLSearchParams): UnifiedReportConfig {
    const viewType = (params.get('viewType') as 'table' | 'chart') || 'table';

    const config: UnifiedReportConfig = {
      viewType,
      filters: {},
    };

    // Chart settings
    if (viewType === 'chart') {
      config.chartSettings = {
        chartType: (params.get('chartType') as ChartType) || 'bar',
        groupByField: params.get('groupByField') || 'application_state',
        dateGrouping: (params.get('dateGrouping') as DateGroupingInterval) || 'day',
        showTableView: params.get('showTableView') === 'true',
      };
    }

    // Table settings
    if (viewType === 'table') {
      config.tableSettings = {
        showIndividualApplications: params.get('showIndividualApplications') === 'true',
      };
    }

    const filterKeys = [
      'programTemplateId',
      'programCreatedAtStart',
      'programCreatedAtEnd',
      'excludeNonVoucherPrograms',
      'courseCashProgramsOnly',
      'applicationCreatedAtStart',
      'applicationCreatedAtEnd',
      'applicationDateRangePreset',
      'inCollections',
      'includePayments',
      'paymentYear',
      'includeCourses',
      'coursesBeginDate',
      'coursesEndDate',
      'employeeId',
      'terminatedEmployees',
      'excludeTermVerified',
      'approverId',
      'timeRange',
      'searchText',
    ];

    filterKeys.forEach((key) => {
      const value = params.get(key);
      if (value !== null) {
        if (
          key === 'excludeNonVoucherPrograms' ||
          key === 'courseCashProgramsOnly' ||
          key === 'inCollections' ||
          key === 'includePayments' ||
          key === 'includeCourses' ||
          key === 'terminatedEmployees' ||
          key === 'excludeTermVerified'
        ) {
          (config.filters as Record<string, unknown>)[key] = value === 'true';
        } else {
          (config.filters as Record<string, unknown>)[key] = value;
        }
      }
    });

    const arrayFilterKeys = ['applicationStates', 'paymentStates'];
    arrayFilterKeys.forEach((key) => {
      const value = params.get(key);
      if (value) {
        (config.filters as Record<string, unknown>)[key] = value.split(',').filter(Boolean);
      }
    });

    return config;
  }

  /**
   * Validate unified config
   */
  static validate(config: UnifiedReportConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate view type
    if (!['table', 'chart'].includes(config.viewType)) {
      errors.push('Invalid view type. Must be "table" or "chart".');
    }

    // Validate chart settings if chart view
    if (config.viewType === 'chart' && config.chartSettings) {
      if (!config.chartSettings.groupByField) {
        errors.push('Chart view requires a groupByField.');
      }
      if (!config.chartSettings.chartType) {
        errors.push('Chart view requires a chartType.');
      }
    }

    // Validate table settings if table view
    if (config.viewType === 'table' && config.tableSettings) {
      // Table settings validation can be added here
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Merge two configs, with the second config taking precedence
   */
  static merge(
    base: UnifiedReportConfig,
    override: Partial<UnifiedReportConfig>
  ): UnifiedReportConfig {
    return {
      ...base,
      ...override,
      chartSettings:
        base.chartSettings && override.chartSettings
          ? {
              ...base.chartSettings,
              ...override.chartSettings,
            }
          : override.chartSettings || base.chartSettings,
      tableSettings:
        base.tableSettings && override.tableSettings
          ? {
              ...base.tableSettings,
              ...override.tableSettings,
            }
          : override.tableSettings || base.tableSettings,
      filters: {
        ...base.filters,
        ...override.filters,
      },
      exportSettings:
        base.exportSettings && override.exportSettings
          ? {
              ...base.exportSettings,
              ...override.exportSettings,
            }
          : override.exportSettings || base.exportSettings,
    };
  }

  /**
   * Create a default configuration
   */
  static createDefault(): UnifiedReportConfig {
    return {
      viewType: 'table',
      tableSettings: {
        showIndividualApplications: false,
      },
      filters: {},
    };
  }

  /**
   * Get column order from config or return empty array
   * Format: snake_case array matching how fields are stored
   */
  static getColumnOrder(config: UnifiedReportConfig): string[] {
    return config.tableSettings?.columnOrder || [];
  }

  /**
   * Update column order in configuration
   * Accepts snake_case array and returns new config object
   */
  static updateColumnOrder(
    config: UnifiedReportConfig,
    newColumnOrder: string[]
  ): UnifiedReportConfig {
    return {
      ...config,
      tableSettings: {
        ...(config.tableSettings || { showIndividualApplications: false }),
        columnOrder: newColumnOrder,
      },
    };
  }
}
